import { FC, useCallback, useEffect, useState } from 'react';

import {
  DashboardRole,
  getDashboardCogsDetail,
  getDashboardSummary,
} from '@shared/api/giftOrder/giftOrder.controller';
import {
  CogsDetailCoupon,
  CogsDetailDelivery,
  DashboardCogsDetailResponse,
  DashboardSummaryResponse,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';
import { Loading } from '@shared/components/Loading';
import { LowerRole } from '@shared/types';
import { formatPrice } from '@shared/utils/formatPrice';

import { fmtDate, fmtPercent, fmtPeriod } from '../utils/dashboard.utils';
import {
  CalcRow,
  CenterLoading,
  EmptyBox,
  ErrorBox,
  StatCard,
  TD_CLASS,
  TH_CLASS,
} from './DashboardShared';

const couponStatusKo: Record<string, string> = {
  Requested: '요청됨',
  Delivered: '전달완료',
  Refunded: '환불완료',
  Available: '사용가능',
  Rejected: '거절됨',
  Expired: '만료됨',
  Canceled: '취소됨',
};

const deliveryStatusKo: Record<string, string> = {
  WaitingAddress: '주소 대기',
  AddressSubmitted: '주소 입력됨',
  Preparing: '준비중',
  Shipping: '배송중',
  Delivered: '배송완료',
  Canceled: '취소됨',
};

export const DashboardSummaryTab: FC<{
  from: string;
  to: string;
  role?: DashboardRole;
}> = ({ from, to, role }) => {
  const [data, setData] = useState<DashboardSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [cogsOpen, setCogsOpen] = useState(false);
  const [cogsDetail, setCogsDetail] = useState<DashboardCogsDetailResponse | null>(null);
  const [cogsLoading, setCogsLoading] = useState(false);
  const [cogsTab, setCogsTab] = useState<'coupon' | 'delivery'>('coupon');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCogsOpen(false);
    setCogsDetail(null);
    try {
      setData(await getDashboardSummary(from, to, role));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '데이터를 불러올 수 없습니다.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, role]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleCogsDetail = useCallback(async () => {
    if (cogsOpen) {
      setCogsOpen(false);
      return;
    }
    setCogsOpen(true);
    if (cogsDetail) return;
    setCogsLoading(true);
    try {
      setCogsDetail(await getDashboardCogsDetail(from, to, role));
    } catch {
      setCogsDetail(null);
    } finally {
      setCogsLoading(false);
    }
  }, [cogsOpen, cogsDetail, from, to, role]);

  if (loading) return <CenterLoading />;
  if (error) return <ErrorBox message={error} onRetry={fetchData} />;
  if (!data) return <EmptyBox />;

  const bp = data.byPaymentDate;
  const cogs = data.cogsByFulfillmentDate;
  const payout = data.payoutToPartnerA;
  const constants = data.constants;

  return (
    <div className="space-y-6">
      {/* 매출·수수료 */}
      <section className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4">
          <h2 className="text-lg font-bold text-gray-800">
            매출 · 수수료
            <span className="ml-2 text-xs font-normal text-gray-400">
              결제일 기준 ({fmtPeriod(from, to)})
            </span>
          </h2>
          <p className="mt-1 text-xs text-gray-400">
            고객이 실제 결제한 날짜 기준으로 집계합니다. 결제 완료된 주문만 포함되며,
            취소분은 차감 반영됩니다.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="주문 건수" value={`${bp.orderCount}건`} />
          <StatCard label="면세 매출" value={`${formatPrice(bp.netTaxFreeSales)}원`} />
          <StatCard label="과세 매출" value={`${formatPrice(bp.netTaxableSales)}원`} />
          <StatCard
            label="총 매출"
            value={`${formatPrice(bp.grossSales)}원`}
            accent="indigo"
          />
        </div>
        <div className="mt-4 border-t border-gray-100 pt-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard
              label="수수료 기준 금액"
              value={`${formatPrice(bp.netCashForServiceFee)}원`}
              sub={`수수료율 ${fmtPercent(constants.serviceFeeRate)}`}
            />
            <StatCard
              label="수수료"
              value={`${formatPrice(bp.serviceFee)}원`}
              sub="면세매출에서 직접 공제"
              accent="red"
            />
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-xs font-medium text-emerald-600">매출 - 수수료</p>
              <p className="mt-1 text-xl font-bold text-emerald-700">
                {formatPrice(bp.balanceBeforeCogs)}원
              </p>
              <div className="mt-1.5 flex gap-3 text-sm font-semibold">
                <span className="text-gray-600">
                  면세{' '}
                  <span className="text-emerald-700">
                    {formatPrice(bp.taxFreeBalance ?? 0)}원
                  </span>
                </span>
                <span className="text-gray-400">/</span>
                <span className="text-gray-600">
                  과세{' '}
                  <span className="text-emerald-700">
                    {formatPrice(bp.taxableBalance ?? 0)}원
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 매입 원가 */}
      <section className="rounded-lg bg-white shadow">
        <div className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                매입 원가
                <span className="ml-2 text-xs font-normal text-gray-400">
                  이행일 기준 ({fmtPeriod(from, to)})
                </span>
              </h2>
              <p className="mt-1 text-xs text-gray-400">
                상품이 실제 전달·배송 처리된 날짜 기준으로 집계합니다. 쿠폰은 전달일 또는
                환불일 기준이며, 배송은 배송 완료일 기준입니다. 취소된 건은 제외됩니다.
              </p>
            </div>
            <button
              onClick={handleToggleCogsDetail}
              className={`ml-4 shrink-0 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                cogsOpen
                  ? 'bg-gray-800 text-white'
                  : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50'
              }`}
            >
              {cogsOpen ? '상세 접기 ▲' : '상세 보기 ▼'}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard
              label="쿠폰 도매가"
              value={`${formatPrice(cogs.couponWholesale)}원`}
              sub={`${cogs.couponCount}건`}
            />
            <StatCard
              label="배송 도매가"
              value={`${formatPrice(cogs.deliveryWholesale)}원`}
              sub={`${cogs.deliveryLineCount}건`}
            />
            <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
              <p className="text-xs font-medium text-orange-600">총 매입 원가</p>
              <p className="mt-1 text-xl font-bold text-orange-600">
                {formatPrice(cogs.totalCogs)}원
              </p>
              <div className="mt-1.5 flex gap-3 text-sm font-semibold">
                <span className="text-gray-600">
                  면세{' '}
                  <span className="text-orange-600">
                    {formatPrice(cogs.taxFreeCogs ?? 0)}원
                  </span>
                </span>
                <span className="text-gray-400">/</span>
                <span className="text-gray-600">
                  과세{' '}
                  <span className="text-orange-600">
                    {formatPrice(cogs.taxableCogs ?? 0)}원
                  </span>
                  <span className="ml-1 text-xs font-normal text-gray-400">
                    (VAT포함)
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        {cogsOpen && (
          <div className="border-t border-gray-100">
            {cogsLoading ? (
              <div className="flex justify-center py-8">
                <Loading />
              </div>
            ) : !cogsDetail ? (
              <div className="py-8 text-center text-sm text-gray-400">
                상세 데이터를 불러올 수 없습니다.
              </div>
            ) : (
              <div className="p-6">
                <div className="mb-4 flex gap-2">
                  <button
                    onClick={() => setCogsTab('coupon')}
                    className={`rounded-lg px-4 py-2 text-sm font-medium ${
                      cogsTab === 'coupon'
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    쿠폰 ({cogsDetail.coupons.length}건)
                  </button>
                  <button
                    onClick={() => setCogsTab('delivery')}
                    className={`rounded-lg px-4 py-2 text-sm font-medium ${
                      cogsTab === 'delivery'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    배송 ({cogsDetail.deliveries.length}건)
                  </button>
                  {(() => {
                    const retailTotal = cogsDetail.coupons.reduce(
                      (s, c) => s + c.priceRetail,
                      0,
                    );
                    const wholesaleTotal = cogsDetail.totalCogs;
                    const discountRate =
                      retailTotal > 0
                        ? ((retailTotal - wholesaleTotal) / retailTotal) * 100
                        : 0;
                    return (
                      <div className="ml-auto flex items-center gap-4 text-sm font-semibold text-gray-700">
                        <span>
                          소비자가{' '}
                          <span className="text-indigo-600">
                            {formatPrice(retailTotal)}원
                          </span>
                        </span>
                        <span>
                          도매가{' '}
                          <span className="text-orange-600">
                            {formatPrice(wholesaleTotal)}원
                          </span>
                        </span>
                        <span className="text-emerald-600">
                          ▼{discountRate.toFixed(1)}%
                        </span>
                      </div>
                    );
                  })()}
                </div>
                {cogsTab === 'coupon' ? (
                  <CouponDetailTable
                    coupons={cogsDetail.coupons}
                    total={cogsDetail.couponTotalWholesale}
                    role={role}
                  />
                ) : (
                  <DeliveryDetailTable
                    deliveries={cogsDetail.deliveries}
                    total={cogsDetail.deliveryTotalWholesale}
                    role={role}
                  />
                )}
                <div className="mt-3 flex gap-4 text-xs text-gray-500">
                  <span>
                    면세 매입 합계:{' '}
                    <span className="font-semibold text-gray-700">
                      {formatPrice(cogsDetail.taxFreeCogs ?? 0)}원
                    </span>
                  </span>
                  <span>
                    과세 매입 합계:{' '}
                    <span className="font-semibold text-gray-700">
                      {formatPrice(cogsDetail.taxableCogs ?? 0)}원
                    </span>
                    <span className="ml-1 text-gray-400">(부가세 포함)</span>
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* 정산 */}
      <section className="rounded-lg bg-white p-6 shadow">
        <h2 className="mb-4 text-lg font-bold text-gray-800">파트너 정산</h2>
        <div className="flex flex-col items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-50 to-emerald-50 p-6">
          <span className="text-sm font-medium text-gray-500">정산 금액</span>
          <span
            className={`text-3xl font-extrabold ${
              payout.amount >= 0 ? 'text-emerald-700' : 'text-red-600'
            }`}
          >
            {formatPrice(payout.amount)}원
          </span>
          <div className="mt-2 flex gap-4 text-base font-semibold">
            <span className="text-gray-600">
              면세{' '}
              <span className="text-emerald-700">
                {formatPrice((bp.taxFreeBalance ?? 0) - (cogs.taxFreeCogs ?? 0))}원
              </span>
            </span>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600">
              과세{' '}
              <span className="text-emerald-700">
                {formatPrice((bp.taxableBalance ?? 0) - (cogs.taxableCogs ?? 0))}원
              </span>
              <span className="ml-1 text-xs font-normal text-gray-400">(VAT포함)</span>
            </span>
          </div>
          <span className="mt-1 text-xs text-gray-400">(매출 - 수수료) - 매입 원가</span>
          <p className="mt-2 rounded bg-amber-50 px-3 py-1.5 text-xs text-amber-700">
            매출·수수료는 결제한 날짜 기준, 매입 원가는 상품이 전달·배송된 날짜 기준이라
            같은 기간이라도 금액이 다를 수 있습니다.
          </p>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              <CalcRow label="총 매출" value={bp.grossSales} />
              <CalcRow label="- 수수료" value={-bp.serviceFee} negative />
              <CalcRow label="= 매출 - 수수료" value={bp.balanceBeforeCogs} bold />
              <CalcRow label="- 총 매입 원가" value={-cogs.totalCogs} negative />
              <CalcRow label="= 정산 금액" value={payout.amount} bold highlight />
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

/* ── 매입 상세 테이블 ── */

const CouponDetailTable: FC<{
  coupons: CogsDetailCoupon[];
  total: number;
  role?: DashboardRole;
}> = ({ coupons, total, role }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-purple-50">
        <tr>
          <th className={TH_CLASS}>#</th>
          <th className={TH_CLASS}>상품명</th>
          {role !== LowerRole.BugoAgencyAdmin && <th className={TH_CLASS}>수령자</th>}
          <th className={TH_CLASS}>전달일</th>
          <th className={TH_CLASS}>구매일</th>
          <th className={TH_CLASS}>상태</th>
          <th className={TH_CLASS}>과세</th>
          <th className={`${TH_CLASS} text-right`}>소비자가</th>
          <th className={`${TH_CLASS} text-right`}>도매가</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 bg-white">
        {coupons.length === 0 ? (
          <tr>
            <td
              colSpan={role === LowerRole.BugoAgencyAdmin ? 8 : 9}
              className="px-3 py-6 text-center text-sm text-gray-400"
            >
              해당 기간 쿠폰 매입 내역이 없습니다.
            </td>
          </tr>
        ) : (
          coupons.map((c, i) => (
            <tr key={c._id} className="hover:bg-gray-50">
              <td className={`${TD_CLASS} text-gray-400`}>{i + 1}</td>
              <td className={TD_CLASS}>{c.giftItemName}</td>
              {role !== LowerRole.BugoAgencyAdmin && (
                <td className={TD_CLASS}>
                  {c.recipientName}
                  {c.recipientPhone && (
                    <span className="ml-1 text-xs text-gray-400">{c.recipientPhone}</span>
                  )}
                </td>
              )}
              <td className={TD_CLASS}>{fmtDate(c.fulfilledAt)}</td>
              <td className={TD_CLASS}>{fmtDate(c.purchasedAt)}</td>
              <td className={TD_CLASS}>
                <span className="rounded bg-purple-100 px-1.5 py-0.5 text-xs font-medium text-purple-700">
                  {couponStatusKo[c.redeemStatus] ?? c.redeemStatus}
                </span>
              </td>
              <td className={TD_CLASS}>
                <span
                  className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                    c.isTaxable
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {c.isTaxable ? '과세' : '면세'}
                </span>
              </td>
              <td className={`${TD_CLASS} text-right`}>{formatPrice(c.priceRetail)}원</td>
              <td className={`${TD_CLASS} text-right font-medium`}>
                {formatPrice(c.priceWhole)}원
              </td>
            </tr>
          ))
        )}
      </tbody>
      {coupons.length > 0 && (
        <tfoot className="bg-purple-50">
          <tr>
            <td
              colSpan={role === LowerRole.BugoAgencyAdmin ? 7 : 8}
              className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600"
            >
              쿠폰 소계
            </td>
            <td className="px-3 py-2.5 text-right text-sm font-bold text-purple-700">
              {formatPrice(total)}원
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  </div>
);

const DeliveryDetailTable: FC<{
  deliveries: CogsDetailDelivery[];
  total: number;
  role?: DashboardRole;
}> = ({ deliveries, total, role }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-blue-50">
        <tr>
          <th className={TH_CLASS}>#</th>
          <th className={TH_CLASS}>상품명</th>
          {role !== LowerRole.BugoAgencyAdmin && <th className={TH_CLASS}>수령자</th>}
          <th className={TH_CLASS}>배송완료일</th>
          <th className={TH_CLASS}>상태</th>
          <th className={TH_CLASS}>과세</th>
          <th className={`${TH_CLASS} text-right`}>수량</th>
          <th className={`${TH_CLASS} text-right`}>단가</th>
          <th className={`${TH_CLASS} text-right`}>도매가</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 bg-white">
        {deliveries.length === 0 ? (
          <tr>
            <td
              colSpan={role === LowerRole.BugoAgencyAdmin ? 8 : 9}
              className="px-3 py-6 text-center text-sm text-gray-400"
            >
              해당 기간 배송 매입 내역이 없습니다.
            </td>
          </tr>
        ) : (
          deliveries.map((d, i) => (
            <tr key={d._id} className="hover:bg-gray-50">
              <td className={`${TD_CLASS} text-gray-400`}>{i + 1}</td>
              <td className={TD_CLASS}>{d.giftItemName}</td>
              {role !== LowerRole.BugoAgencyAdmin && (
                <td className={TD_CLASS}>
                  {d.recipientName}
                  {d.recipientPhone && (
                    <span className="ml-1 text-xs text-gray-400">{d.recipientPhone}</span>
                  )}
                </td>
              )}
              <td className={TD_CLASS}>{fmtDate(d.deliveredAt)}</td>
              <td className={TD_CLASS}>
                <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">
                  {deliveryStatusKo[d.deliveryStatus] ?? d.deliveryStatus}
                </span>
              </td>
              <td className={TD_CLASS}>
                <span
                  className={`rounded px-1.5 py-0.5 text-xs font-medium ${
                    d.isTaxable
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {d.isTaxable ? '과세' : '면세'}
                </span>
              </td>
              <td className={`${TD_CLASS} text-right`}>{d.quantity}</td>
              <td className={`${TD_CLASS} text-right`}>{formatPrice(d.priceWhole)}원</td>
              <td className={`${TD_CLASS} text-right font-medium`}>
                {formatPrice(d.lineCogs)}원
              </td>
            </tr>
          ))
        )}
      </tbody>
      {deliveries.length > 0 && (
        <tfoot className="bg-blue-50">
          <tr>
            <td
              colSpan={role === LowerRole.BugoAgencyAdmin ? 7 : 8}
              className="px-3 py-2.5 text-right text-xs font-semibold text-gray-600"
            >
              배송 소계
            </td>
            <td className="px-3 py-2.5 text-right text-sm font-bold text-blue-700">
              {formatPrice(total)}원
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  </div>
);
